import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth as useClerkAuth, useUser, useClerk } from '@clerk/react'
import api from '@/api/client'
import { setTokenProvider } from '@/api/client'
import { HIDE_LEGACY_PAGES } from '@/config/legacy-pages'
import { useServerAccess } from './useServerAccess.js'
import { effectiveRole } from './access-model.js'
import MeErrorNotice from '@/components/engine/access/MeErrorNotice'

const AuthContext = createContext(null)

/**
 * Auth Provider — Clerk handles authentication.
 * Phase 2: Replaces Cloudflare Access with Clerk JWT.
 *
 * The role comes from the Engine server (GET /api/me, hooks/useServerAccess.js):
 *   owner -> admin, operator -> operator, restricted_operator -> viewer.
 * Clerk publicMetadata.role is only the fallback while that route answers 404 or 503 (not connected).
 * While the answer loads, or when it fails, the role is the least privilege, never an open screen.
 */
export function AuthProvider({ children }) {
  const { isSignedIn, isLoaded: authLoaded, getToken, sessionId } = useClerkAuth()
  const { user, isLoaded: userLoaded } = useUser()
  const { signOut } = useClerk()

  const [legacyRole, setLegacyRole] = useState('viewer')
  const [apiError, setApiError] = useState(null)

  const isLoading = !authLoaded || !userLoaded
  const isAuthenticated = isSignedIn === true

  // Inject Clerk token provider into API client (bridges React → vanilla JS)
  useEffect(() => {
    if (getToken) {
      setTokenProvider(getToken)
    }
  }, [getToken])

  // The old source: Clerk publicMetadata, used only behind the not connected state
  useEffect(() => {
    if (user?.publicMetadata?.role) {
      setLegacyRole(user.publicMetadata.role)
    } else {
      setLegacyRole('viewer') // Default to least privilege
    }
  }, [user])

  // Optionally verify against backend /api/auth/me on first load.
  // Skipped while legacy pages are hidden: /api/auth/me is on the legacy admin API, which
  // does not accept calls from the Engine console host. The role then comes from Clerk
  // publicMetadata only. It only hides screens in the browser; the Engine server enforces roles.
  useEffect(() => {
    if (HIDE_LEGACY_PAGES) return
    if (!isAuthenticated || isLoading) return
    api.get('/api/auth/me').then(data => {
      if (data?.role && data.role !== legacyRole) {
        setLegacyRole(data.role)
      }
      setApiError(null)
    }).catch(err => {
      console.error('Auth/me check failed:', err.message)
      setApiError(err.message)
    })
  }, [isAuthenticated, isLoading])

  const server = useServerAccess({ enabled: isAuthenticated && !isLoading, getToken, userId: user?.id, sessionId, baseUrl: import.meta.env.VITE_ENGINE_API_URL, allowLocal: import.meta.env.DEV })
  const role = effectiveRole({ meState: server.meState, access: server.access, legacyRole })

  const logout = useCallback(() => {
    signOut({ redirectUrl: window.location.origin })
  }, [signOut])

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      error: apiError,
      user,
      role,
      meState: server.meState,
      meError: server.meError,
      access: server.access,
      accessClient: server.client,
      setWorkspaceId: server.setWorkspaceId,
      retryMe: server.retryMe,
      getToken,
      sessionId,
      logout
    }}>
      {children}
      {server.meState === 'error' && <MeErrorNotice message={server.meError} onRetry={server.retryMe} />}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
